import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Grid,
  IconButton,
  InputAdornment,
  useMediaQuery,
  Box,
} from "@mui/material";
import * as Yup from "yup";
import { useFormik } from "formik";
import EmojiObjectsIcon from "@mui/icons-material/EmojiObjects";
import RadioIcon from "@mui/icons-material/Radio";
import ComputerIcon from "@mui/icons-material/Computer";
import IronIcon from "@mui/icons-material/Iron";
import BlenderIcon from "@mui/icons-material/Blender";
import TvIcon from "@mui/icons-material/Tv";
import MicrowaveIcon from "@mui/icons-material/Microwave";
import DevicesIcon from "@mui/icons-material/Devices";

import InputFieldText from "Components/Common/InputFieldText";
import SelectField from "Components/Common/SelectField";
import BaseTable from "Components/Common/BaseTable";
import Loader from "Components/Common/Loader";
import FooterSection from "./Footer";
import HeaderSection from "./Header";

import { onlyNumberKeys } from "utils";

import { useGetMenuItemsQuery } from "apis/HomePage";

import {
  CeillingFanIcon,
  TableFanIcon,
  GeyserIcon,
  WaterHeaterIcon,
  StorageHeaterIcon,
  WashingMachineIcon,
  AirCoditionerIcon,
  AirCoolerIcon,
  ReferigeratorIcon,
  WaterPurifierIcon,
  VaccumeCleanerIcon,
  ElectricKitliIcon,
  HotPlateIcon,
  ToasterIcon,
  WaterPumpIcon,
  TapeRecoderIcon,
  TubeLightIcon,
  GharGhantiIcon,
  FlrLampIcon,
} from "./style";

const appliancesOptions = [
  {
    value: 1,
    label: "Lamp",
    icon: <EmojiObjectsIcon color="primary" />,
  },
  {
    value: 2,
    label: "Ceiling Fan",
    icon: <CeillingFanIcon />,
  },
  { value: 3, label: "Table Fan", icon: <TableFanIcon /> },
  { value: 4, label: "Electric Iron", icon: <IronIcon color="primary" /> },
  { value: 5, label: "Geyser", icon: <GeyserIcon /> },
  { value: 6, label: "Water Heater", icon: <WaterHeaterIcon /> },
  { value: 7, label: "Storage Heater", icon: <StorageHeaterIcon /> },
  { value: 8, label: "Mixer", icon: <BlenderIcon color="primary" /> },
  { value: 9, label: "Washing M/C", icon: <WashingMachineIcon /> },
  { value: 10, label: "Air Conditioner", icon: <AirCoditionerIcon /> },
  { value: 11, label: "Air Cooler", icon: <AirCoolerIcon /> },
  { value: 12, label: "Refrigerator", icon: <ReferigeratorIcon /> },
  { value: 13, label: "Computer", icon: <ComputerIcon color="primary" /> },
  { value: 14, label: "T.V", icon: <TvIcon color="primary" /> },
  { value: 15, label: "Radio", icon: <RadioIcon color="primary" /> },
  { value: 16, label: "Tape Recorder", icon: <TapeRecoderIcon /> },
  { value: 17, label: "Water Pump", icon: <WaterPumpIcon /> },
  { value: 18, label: "Flr. Tube Light(48inch)", icon: <TubeLightIcon /> },
  { value: 19, label: "Flr. Lamp", icon: <FlrLampIcon /> },
  { value: 20, label: "Toaster", icon: <ToasterIcon /> },
  { value: 21, label: "Oven", icon: <MicrowaveIcon color="primary" /> },
  { value: 22, label: "Hot Plate", icon: <HotPlateIcon /> },
  { value: 23, label: "Elec. Kitli", icon: <ElectricKitliIcon /> },
  { value: 24, label: "Ghar Ghanti", icon: <GharGhantiIcon /> },
  { value: 25, label: "Vacuum Cleaner", icon: <VaccumeCleanerIcon /> },
  { value: 26, label: "Water Purifier", icon: <WaterPurifierIcon /> },
  { value: 0, label: "Other" },
];

const columnProps = { style: { width: "250px", minWidth: "250px" } };

const appliencesList = (appliences, number, otherAppliances) => {
  return (
    <Box sx={{ "& > img": { mr: 2, flexShrink: 0 } }}>
      <IconButton>
        {otherAppliances ? (
          <DevicesIcon color="primary" />
        ) : (
          appliancesOptions.find((option) => option.value === appliences).icon
        )}
      </IconButton>
      {(otherAppliances
        ? otherAppliances
        : appliancesOptions.find((option) => option.value === appliences)
            .label) +
        " * " +
        `${number}`}
    </Box>
  );
};

const columns = [
  {
    title: "Appliances",
    renderCell: ({ appliences, number, otherAppliances }) =>
      appliencesList(appliences, number, otherAppliances),
    columnProps: { style: { width: "350px", minWidth: "350px" } },
  },
  {
    title: "Watt",
    field: "watt",
    columnProps,
  },
  {
    title: "Hrs.",
    field: "hours",
    columnProps,
  },
  {
    title: "Units/Month",
    field: "unitsPerMonths",
    columnProps,
  },
];

const EnergyCalculator = () => {
  const [tableData, setTableData] = useState([]);

  const { data: { data: menuItems = [] } = {}, isLoading: isMenuDataLoading } =
    useGetMenuItemsQuery();

  const sm = useMediaQuery("(max-width: 599px)");

  const formik = useFormik({
    initialValues: {
      selectedAppliances: "",
      selectedWatt: "",
      selectedNumber: 1,
      selectedDailyUsage: 0.5,
      selectedOtherAppliances: "",
    },
    validationSchema: Yup.object({
      selectedAppliances: Yup.number().required("Appliances is required"),
      selectedNumber: Yup.number().required("Number is required"),
      selectedWatt: Yup.string().required("Watt is required"),
      selectedDailyUsage: Yup.number()
        .max(24, "Daily Usage must be less than or equal to 24 hrs")
        .required("Daily Usage is required"),
      selectedOtherAppliances: Yup.string().when("selectedAppliances", {
        is: (selectedAppliances) => selectedAppliances === 0,
        then: (schema) => schema.required("Other Appliances is required"),
        otherwise: (schema) => schema,
      }),
    }),
    onSubmit: (
      {
        selectedAppliances,
        selectedNumber,
        selectedWatt,
        selectedDailyUsage,
        selectedOtherAppliances,
      },
      { resetForm }
    ) => {
      const data = {
        appliences: selectedAppliances,
        otherAppliances: selectedOtherAppliances,
        number: selectedNumber,
        watt: +(selectedWatt * selectedNumber).toFixed(2),
        hours: selectedDailyUsage.toFixed(2),
        unitsPerMonths: +(
          (+selectedWatt * +selectedNumber * +selectedDailyUsage * 30) /
          1000
        ).toFixed(2),
      };
      setTableData((prev) => [...prev, data]);
      resetForm();
    },
  });

  const {
    values: { selectedAppliances },
    handleSubmit,
    resetForm,
    setFieldValue,
  } = formik;

  const resetBtnHandler = () => {
    setTableData([]);
    resetForm();
  };

  useEffect(() => {
    if (selectedAppliances) {
      setFieldValue("selectedOtherAppliances", "");
    }
  }, [selectedAppliances]);

  const totalUnitsPerMonths = useMemo(
    () =>
      tableData.reduce((acc, curr) => acc + curr.unitsPerMonths, 0).toFixed(2),
    [tableData]
  );

  if (isMenuDataLoading) return <Loader />;

  return (
    <div className="d-flex flex-column justify-content-between min-vh-100">
      <div className="header-nav-wrapper nav-background">
        <div className="container-fluid">
          <HeaderSection menuItems={menuItems} isGiftInfo />
        </div>
      </div>

      <div
        className="container-fluid vision-section"
        style={{ paddingTop: "130px" }}
      >
        <div className="fs-4 fw-semibold d-flex justify-content-center">
          Energy Usage Calculator
        </div>
        <Grid
          container
          columnSpacing={2}
          className="mb-1 mt-3 d-flex justify-content-center"
        >
          <Grid item lg={2} md={4} sm={6} xs={12}>
            <SelectField
              label="Select Appliances"
              name="selectedAppliances"
              options={appliancesOptions}
              formik={formik}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  sx={{ "& > img": { mr: 2, flexShrink: 0 } }}
                  {...props}
                >
                  <IconButton>{option.icon}</IconButton>
                  {option.label}
                </Box>
              )}
            />
          </Grid>
          {selectedAppliances === 0 && (
            <Grid item lg={2} md={4} sm={6} xs={12}>
              <InputFieldText
                label="Enter Other Appliances"
                name="selectedOtherAppliances"
                placeholder="Enter Other Appliances"
                formik={formik}
              />
            </Grid>
          )}
          <Grid item lg={2} md={4} sm={6} xs={12}>
            <InputFieldText
              label="Watt"
              name="selectedWatt"
              placeholder="Enter Watt"
              formik={formik}
              type="number"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">Watt</InputAdornment>
                ),
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
            />
          </Grid>
          <Grid item lg={2} md={4} sm={6} xs={12}>
            <InputFieldText
              label="Number"
              name="selectedNumber"
              placeholder="Enter Number"
              formik={formik}
              type="number"
              InputProps={{
                inputProps: { min: 0, onKeyDown: onlyNumberKeys },
              }}
            />
          </Grid>
          <Grid item lg={2} md={4} sm={6} xs={12}>
            <InputFieldText
              label="Daily Usage (Hrs.)"
              name="selectedDailyUsage"
              type="number"
              formik={formik}
              InputProps={{
                inputProps: {
                  min: 0,
                  onKeyDown: (e) => onlyNumberKeys(e, true),
                },
              }}
              placeholder="Enter Daily Usage (Hrs.)"
            />
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              className="mb-2 me-2"
              type="submit"
              onClick={handleSubmit}
            >
              Add
            </Button>
            <Button
              variant="outlined"
              onClick={resetBtnHandler}
              className="mb-2 me-2"
            >
              Reset
            </Button>
          </Grid>
          <BaseTable
            columns={columns}
            data={tableData}
            pageSize={20}
            displaySrNo
            maxHeight={sm ? "calc(100vh - 300px)" : "calc(100vh - 250px)"}
          />
        </Grid>

        <div className="fs-4 d-flex justify-content-center mt-2">
          Total : {totalUnitsPerMonths} Units/month
        </div>
      </div>

      <FooterSection menuItems={menuItems} isGiftInfo />
    </div>
  );
};

export default EnergyCalculator;
